import { Box, Center, Icon, Image, Spinner } from '@chakra-ui/react'
import type { ReactElement } from 'react'
import { useContext, useState } from 'react'
import { checkOperationPermission, PermissionsEnum } from '../../../../utils'
import { ACLPermissionEnum } from '../../../constants'
import type { MediaLibrarySearchFileResponse } from '../../../interfaces'
import { UserContext } from '../../../providers'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'
import PermissionGuard from '../../PermissionGuard'
import { ThreadAvatarOverlay } from '../Details/Threads'
import LazyNewThreadPanel from '../Details/Threads/LazyNewThreadPanel'
import { getExtensionFromFileTypeAndMimeType } from '../../../../utils/mediaLibrary'
import GenericIcon from '../../../../graphics/media-library/file-icons/GenericIcon'
import type { FilePreviewProps } from '.'

const IconPreview = ({
  file,
  showPreview,
  showThreads = false,
  disablePadding,
}: FilePreviewProps): ReactElement => {
  const { thumbnail } = file
  const extension = getExtensionFromFileTypeAndMimeType(file)
  const { user } = useContext(UserContext)
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const [loading, setLoading] = useState<boolean>(true)
  let thumbnailUrlToUse = thumbnail
  const defaultFileIcon =
    !thumbnail ||
    (thumbnail &&
      thumbnail ===
        'https://ik.imagekit.io/ikmedia/static-icons/GenericIcon.svg')
  if (extension === 'mov') {
    thumbnailUrlToUse =
      'https://ik.imagekit.io/ikmedia/static-icons/VideoIcon.svg'
  }
  if (!defaultFileIcon) {
    return (
      <Box position="relative">
        <Center>
          <Image
            draggable={false}
            onDragStart={e => e.preventDefault()}
            sx={{ WebkitUserDrag: 'none' }}
            src={thumbnailUrlToUse}
            boxSize="100%"
            padding={disablePadding ? '0' : '2em'}
            onLoad={() => {
              setLoading(false)
            }}
            onError={() => {
              setLoading(false)
            }}
            fallback={
              loading ? (
                <Center>
                  <Spinner />
                </Center>
              ) : (
                <Icon
                  as={GenericIcon}
                  boxSize="100%"
                  padding={disablePadding ? '0' : '2em'}
                />
              )
            }
          />
        </Center>
        {showThreads ? (
          <>
            <LazyNewThreadPanel
              fileDetails={file}
              position={null}
              setThreadPanelPosition={() => {}}
            />
            <ThreadAvatarOverlay fileDetails={file} />
          </>
        ) : null}
      </Box>
    )
  }

  return (
    <Box position="relative">
      <Center>
        <Image
          draggable={false}
          onDragStart={e => e.preventDefault()}
          sx={{ WebkitUserDrag: 'none' }}
          src={thumbnailUrlToUse}
          boxSize={showPreview ? '400%' : '100%'}
          padding={disablePadding ? '0' : '2em'}
          onLoad={() => {
            setLoading(false)
          }}
          onError={() => {
            setLoading(false)
          }}
          fallback={
            loading ? (
              <Center>
                <Spinner />
              </Center>
            ) : (
              <Icon
                draggable={false}
                onDragStart={e => e.preventDefault()}
                sx={{ WebkitUserDrag: 'none' }}
                as={GenericIcon}
                boxSize={showPreview ? '400%' : '100%'}
                padding={disablePadding ? '0' : '2em'}
              />
            )
          }
        />
      </Center>
      {showThreads && !isPublicLink ? (
        <>
          <PermissionGuard
            permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
          >
            {(hasPermission: boolean) =>
              hasPermission &&
              checkOperationPermission({
                user,
                aclPermission: [
                  ACLPermissionEnum.CONTRIBUTE,
                  ACLPermissionEnum.MANAGE,
                ],
                selectedFiles: [file] as MediaLibrarySearchFileResponse[],
                selectedFolders: [],
                selectedMediaCollections: [],
              }) ? (
                <LazyNewThreadPanel
                  fileDetails={file}
                  position={null}
                  setThreadPanelPosition={() => {}}
                />
              ) : (
                <></>
              )
            }
          </PermissionGuard>
          <ThreadAvatarOverlay fileDetails={file} />
        </>
      ) : null}
    </Box>
  )
}

export default IconPreview
